import type { ColumnConfigEntry } from '@erp/shared-types';

/**
 * How `useColumnConfig` combines a user's saved columns with the defaults.
 *
 * - `legacy` (default): the long-standing rules for tables with hardcoded
 *   defaults — prepend defaults to legacy custom-only records, and re-add
 *   hidden filter-driving defaults the saved config predates.
 * - `addNewDefaults`: for admin-editable defaults (task pages). A default the
 *   saved config has never seen is inserted at its default position; saved
 *   order, visibility and headers stay the user's. Removing a default column
 *   saves it as hidden so the removal sticks.
 * - `ignore`: saved configs are not used (the admin locked the columns).
 */
export type SavedColumnsMode = 'legacy' | 'addNewDefaults' | 'ignore';

/**
 * Legacy records (old code) only contain custom columns — detect this by
 * checking whether any stored column ID matches a default column ID, and
 * prepend defaults when none match. Hidden default columns are always merged
 * in so they can drive filters even if the saved config predates them.
 */
function mergeLegacy(
  stored: ColumnConfigEntry[],
  defaults: ColumnConfigEntry[],
): ColumnConfigEntry[] {
  if (defaults.length === 0) return stored;
  const defaultIds = new Set(defaults.map((c) => c.id));
  if (!stored.some((c) => defaultIds.has(c.id))) {
    return [...defaults, ...stored];
  }
  const storedIds = new Set(stored.map((c) => c.id));
  const missingHidden = defaults.filter(
    (c) => c.hidden && !storedIds.has(c.id),
  );
  return missingHidden.length > 0 ? [...stored, ...missingHidden] : stored;
}

/**
 * Insert each default missing from `stored` right after the nearest earlier
 * default the user still has (or at the start), keeping the user's order.
 */
export function addNewDefaults(
  stored: ColumnConfigEntry[],
  defaults: ColumnConfigEntry[],
): ColumnConfigEntry[] {
  const storedIds = new Set(stored.map((c) => c.id));
  if (defaults.every((c) => storedIds.has(c.id))) return stored;
  const result = [...stored];
  let anchorId: string | null = null;
  for (const column of defaults) {
    if (storedIds.has(column.id)) {
      anchorId = column.id;
      continue;
    }
    const anchorIndex =
      anchorId === null ? -1 : result.findIndex((c) => c.id === anchorId);
    result.splice(anchorIndex + 1, 0, column);
    anchorId = column.id;
  }
  return result;
}

/** Columns to show for a table, given saved + default columns and the mode. */
export function resolveColumns({
  stored,
  defaults,
  isLoading,
  mode,
}: {
  stored: ColumnConfigEntry[];
  defaults: ColumnConfigEntry[];
  isLoading: boolean;
  mode: SavedColumnsMode;
}): ColumnConfigEntry[] {
  if (mode === 'ignore' || stored.length === 0) {
    return isLoading ? [] : defaults;
  }
  return mode === 'addNewDefaults'
    ? addNewDefaults(stored, defaults)
    : mergeLegacy(stored, defaults);
}

/**
 * Remove a column. In `addNewDefaults` mode a default column is kept as
 * hidden instead, so the next merge does not bring it back.
 */
export function removeColumnFrom(
  columns: ColumnConfigEntry[],
  id: string,
  defaults: ColumnConfigEntry[],
  mode: SavedColumnsMode,
): ColumnConfigEntry[] {
  if (mode === 'addNewDefaults' && defaults.some((c) => c.id === id)) {
    return columns.map((c) => (c.id === id ? { ...c, hidden: true } : c));
  }
  return columns.filter((c) => c.id !== id);
}
